import React from 'react';
import { classNames } from '../../../../utils';
import { ModelGradientProps } from '../ModelGradient.types';

export function AudioModelGradient({
    colors,
    rotate,
    animate,
    scale,
    borderRadius: wrapperBorderRadius,
}: Omit<ModelGradientProps, 'gradient'>) {
    const [mainColor, secondaryColor, lightColor] = colors;
    let scaleValue = 1;
    if (scale && scale !== 'none') {
        scaleValue = {
            s: 0.9,
            m: 1.2,
            l: 1.3,
            xl: 1.4,
        }[scale];
    }

    const gradientWrapperStyle = {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: wrapperBorderRadius,
        ['--main-color' as string]: mainColor,
        ['--secondary-color' as string]: secondaryColor,
        ['--light-color' as string]: lightColor ?? '#cdcdcd', // TODO: remove this fallback?,
        background: `linear-gradient(90deg, var(--main-color) 0%, var(--secondary-color) 50%, var(--light-color) 100%)`,
    };

    const shouldStretchGradient = rotate === 90 || rotate === 270;
    const borderRadius = '417px';
    const gradientStyle = {
        ['--rotate' as string]: rotate,
        ['--scale' as string]: scaleValue,
        width: '100%',
        height: shouldStretchGradient ? '200%' : '100%',
        display: 'flex',
        gap: 0,
        background: 'none',
        padding: 0,
        transformOrigin: 'center',
        scale: `calc(var(--scale, 1))`,
        rotate: `calc(var(--rotate) * 1deg)`,
        borderRadius,
    };

    const sharedGradientStyle = ({ mainFirst = false }: { mainFirst: boolean }) => ({
        background: mainFirst
            ? 'linear-gradient(90deg, var(--main-color) 0%, var(--secondary-color) 25%, var(--light-color) 50%, var(--secondary-color) 75%, var(--main-color) 100%)'
            : 'linear-gradient(90deg, var(--secondary-color) 0%, var(--secondary-color) 25%, var(--light-color) 50%, var(--secondary-color) 75%, var(--secondary-color) 100%)',
    });

    const extendedRectangleStyle = {
        position: 'absolute',
        width: '140%',
        height: '90%',
        left: '-20%',
        top: '50%',
        transform: 'translateY(-50%)',
        borderRadius,
        opacity: 0.9,
        background: 'linear-gradient(90deg, var(--main-color) 0%, var(--secondary-color) 50%, var(--light-color) 100%)',
    };

    const innerRectangleStyle = {
        position: 'absolute',
        width: '85%',
        height: '70%',
        left: '7.5%',
        top: '50%',
        transform: 'translateY(-50%)',
        borderRadius,
        opacity: 0.95,
        ...sharedGradientStyle({ mainFirst: true }),
    };

    const innermostRectangleStyle = {
        position: 'absolute',
        width: '70%',
        height: '70%',
        left: '15%',
        top: '50%',
        transform: 'translateY(-50%)',
        borderRadius,
        opacity: 1,
        ...sharedGradientStyle({ mainFirst: true }),
    };

    const ballRectangleStyle = {
        position: 'absolute',
        width: '25%',
        height: '50%',
        left: '37.5%',
        top: '50%',
        transform: 'translateY(-50%)',
        borderRadius,
        opacity: 1,
        background: 'linear-gradient(90deg, var(--main-color) 0%, var(--secondary-color) 50%, var(--light-color) 100%)',
    };

    return (
        <div style={gradientWrapperStyle}>
            <div
                style={gradientStyle}
                className={classNames(
                    animate === 'hover' && 'voice-over-model-gradient-animate-hover',
                    animate === 'always' && 'voice-over-model-gradient-animate-always'
                )}
            >
                <div style={extendedRectangleStyle as React.CSSProperties}>
                    <div style={innerRectangleStyle as React.CSSProperties}>
                        <div style={innermostRectangleStyle as React.CSSProperties}>
                            <div style={ballRectangleStyle as React.CSSProperties}></div>
                        </div>
                    </div>
                </div>
            </div>
            <style>
                {`
.voice-over-model-gradient-animate-hover:hover {
    cursor: pointer;
}

.voice-over-model-gradient-animate-hover div {
    animation: scaleRectangle 4s ease-in-out infinite;
    animation-play-state: paused;
    transform: translateY(-50%) scaleX(1);
    opacity: 0.9;
    transition: all 0.3s ease-out;
}

.voice-over-model-gradient-animate-hover:hover div {
    animation-play-state: running;
}

.voice-over-model-gradient-animate-always div {
    animation: scaleRectangle 4s ease-in-out infinite;
    animation-play-state: running;
    animation-delay: 1.8s;
}

.voice-over-model-gradient-animate-hover div div {
    animation: scaleRectangle 4s ease-in-out infinite;
    animation-play-state: paused;
    transform: translateY(-50%) scaleX(1);
    opacity: 0.9;
    transition: all 0.3s ease-out;
}

.voice-over-model-gradient-animate-hover:hover div div {
    animation-play-state: running;
}

.voice-over-model-gradient-animate-always div div {
    animation: scaleRectangle 4s ease-in-out infinite;
    animation-play-state: running;
    animation-delay: 1.2s;
}

.voice-over-model-gradient-animate-hover div div div {
    animation: scaleRectangle 4s ease-in-out infinite;
    animation-play-state: paused;
    transform: translateY(-50%) scaleX(1);
    opacity: 0.9;
    transition: all 0.3s ease-out;
}

.voice-over-model-gradient-animate-hover:hover div div div {
    animation-play-state: running;
}

.voice-over-model-gradient-animate-always div div div {
    animation: scaleRectangle 4s ease-in-out infinite;
    animation-play-state: running;
    animation-delay: 0.6s;
}

.voice-over-model-gradient-animate-hover div div div div {
    animation: scaleBallRectangle 3s ease-in-out infinite;
    animation-play-state: paused;
    transform: translateY(-50%) scale(1);
    opacity: 0.9;
    transition: all 0.3s ease-out;
}

.voice-over-model-gradient-animate-hover:hover div div div div {
    animation-play-state: running;
}

.voice-over-model-gradient-animate-always div div div div {
    animation: scaleBallRectangle 3s ease-in-out infinite;
    animation-play-state: running;
    animation-delay: 0s;
}

@keyframes scaleRectangle {
    0% {
        transform: translateY(-50%) scaleX(1);
        opacity: 0.9;
    }
    50% {
        transform: translateY(-50%) scaleX(1.1);
        opacity: 1;
    }
    100% {
        transform: translateY(-50%) scaleX(1);
        opacity: 0.9;
    }
}

@keyframes scaleBallRectangle {
    0% {
        transform: translateY(-50%) scale(1);
        opacity: 0.9;
    }
    50% {
        transform: translateY(-50%) scale(1.2);
        opacity: 1;
    }
    100% {
        transform: translateY(-50%) scale(1);
        opacity: 0.9;
    }
}
                `}
            </style>
        </div>
    );
}
